import { FilterGroup, FilterItem } from '@common-modules/product-catalogue';
import {
  FormattedProduct,
  GetFilterDataParams,
  GetProductListParams
} from '../types/productCatalogs.types';
import CATALOGUE from '../constants/catalogue';
import trans from '../hooks/useTranslation';
import { Plan } from '../types/plan.types';
import { KeyValue } from '../types/common.types';
import formatPrice from './formatPrice';
import {
  getFiltersFromQueryParams,
  getRibbon,
  sortByRankingAndTitle
} from './catalogue-helpers';
import { phoneTagsConfig } from '../config/phone-config';
import slugify from 'slugify';
import { URL_PARAMS_MAPPING } from '../config/common';
import { Product } from '../types/product.types';
import CONSTANTS from '../constants/common';
import { CisPlanRates } from '../types/user.types';

const { t } = trans();

export const getFilterFromUrlParams = (
  queryParams: KeyValue = {},
  filterData: FilterGroup[]
): FilterGroup[] => {
  if (filterData?.length) {
    return filterData.map(filterGroup => {
      const paramsIndex = Object.keys(queryParams).indexOf(filterGroup.type);
      if (paramsIndex >= 0) {
        const valueFromUrl = (queryParams[filterGroup.type] as string)
          ?.toLowerCase()
          ?.split(',');
        filterGroup.items.forEach(item => {
          item.selected =
            item.selected ||
            valueFromUrl?.includes(slugify(item.name.toLowerCase()));
        });
      }
      return filterGroup;
    });
  }
  return Object.keys(queryParams).reduce((result: FilterGroup[], key) => {
    const filterValue = queryParams[key]?.toLowerCase() as string;
    const filterItems: FilterItem[] = filterValue.split(',').map(value => {
      return {
        selected: true,
        value: URL_PARAMS_MAPPING[value] || value,
        name: value
      };
    });
    result.push({
      type: key,
      id: key,
      items: filterItems
    } as FilterGroup);
    return result;
  }, []);
};

export const formatProducts = ({
  selectedPlan,
  products,
  simOnlyPlans
}: GetProductListParams): FormattedProduct[] => {
  const { mecId, selectedMonthlyTerm } = selectedPlan;
  const simsByMecId = simOnlyPlans.map(({ mecId }) => mecId);
  return products.map(product => {
    const filteredVariants = product.variants.filter(({ planId }) =>
      simsByMecId.includes(planId)
    );
    const matchingVariant = filteredVariants.find(
      ({ planId, monthlyTerm }) =>
        planId === mecId && monthlyTerm === selectedMonthlyTerm
    );

    const lowestSkuPrice = getLowestSkuPrice(product, selectedPlan);

    const lowestPartialUpfrontPrice = Math.min(
      ...filteredVariants
        .filter(({ planId }) => planId === mecId)
        .map(({ partialUpfrontAmount }) => partialUpfrontAmount)
    );

    const priceText = formatPrice(lowestSkuPrice, 0, '');
    let customPrice;

    const ribbonForPlan = getRibbon(product.ribbon, selectedPlan);
    if (
      matchingVariant?.installmentPrice >= 0 &&
      (!lowestPartialUpfrontPrice || lowestPartialUpfrontPrice >= 0)
    ) {
      const installmentPriceText = t('INSTALLMENT_PRICE_TEXT', [
        formatPrice(matchingVariant?.installmentPrice),
        matchingVariant?.monthlyTerm
      ]);
      customPrice = installmentPriceText;
    }

    return {
      ...product,
      brandId: product.id,
      ribbon: ribbonForPlan?.title,
      heroImage: product.heroImage,
      deviceTitle: product.title,
      priceFrom: priceText,
      customPrice,
      lowestPrice: lowestSkuPrice,
      variants: undefined
    };
  });
};

export const getProductList = (
  params: GetProductListParams
): { filteredProduct: FormattedProduct[]; allProduct: FormattedProduct[] } => {
  const { queryParams, products, filterData, selectedPlan } = params;
  const filterDataFromUrlParams = getFilterFromUrlParams(
    queryParams,

    filterData
  );
  const filteredProductByUrlParams = filterProduct({
    products,
    filterData: filterDataFromUrlParams,
    selectedPlan
  });
  return {
    filteredProduct: formatProducts({
      ...params,
      products: filteredProductByUrlParams
    }),
    allProduct: formatProducts({ ...params, getAllProduct: true })
  };
};

export const isProductHasTag = (
  product: FormattedProduct,
  tag: string
): boolean => {
  return product.tags.some(productTag =>
    productTag.toLowerCase().includes(tag.toLowerCase())
  );
};

export const getFilterValueByType = (
  filterData: FilterGroup[] = [],
  type: string
): string[] => {
  return (
    filterData
      .find(filterGroup => filterGroup.type === type)
      ?.items?.filter(filterItem => filterItem.selected) || []
  ).map(item => item.value.toLowerCase());
};

export const getSelectedFilters = (
  filterData: FilterGroup[]
): {
  planFilters: string[];
  categoryFilters: string[];
  brandFilters: string[];
} => {
  return {
    planFilters: getFilterValueByType(filterData, CATALOGUE.FILTER_TYPES.PLAN),
    categoryFilters: getFilterValueByType(
      filterData,
      CATALOGUE.FILTER_TYPES.CATEGORY
    ),
    brandFilters: getFilterValueByType(filterData, CATALOGUE.FILTER_TYPES.BRAND)
  };
};

export const getFilterDataWithStatus = ({
  filterData,
  products,
  allProducts,
  selectedPlan
}: {
  filterData: FilterGroup[];
  products: FormattedProduct[];
  allProducts: FormattedProduct[];
  selectedPlan: Plan;
}): FilterGroup[] => {
  return filterData.map(filterGroup => {
    filterGroup.items = filterGroup.items.map(filterItem => {
      switch (filterGroup.type) {
        case CATALOGUE.FILTER_TYPES.CATEGORY: {
          const disabled = !products.some(product =>
            isProductHasTag(product, filterItem.value)
          );
          return {
            ...filterItem,
            disabled,
            selected: filterItem.selected && !disabled
          };
        }
        case CATALOGUE.FILTER_TYPES.BRAND: {
          const productFilteredByCategory = filterProduct({
            products: allProducts,
            filterData,
            selectedPlan,
            options: {
              byPlan: true,
              byCategory: true,
              byBrand: false
            }
          });
          const disabled = !productFilteredByCategory.some(
            product =>
              product.brand?.toLowerCase() === filterItem.value?.toLowerCase()
          );
          return {
            ...filterItem,
            disabled,
            selected: filterItem.selected && !disabled
          };
        }
        default: {
          return filterItem;
        }
      }
    });
    return filterGroup;
  });
};

export const getFilterData = (params: GetFilterDataParams): FilterGroup[] => {
  const { categories, brands, queryParams, ippOptions } = params || {};
  const ippUrlParams =
    (queryParams[CATALOGUE.FILTER_TYPES.PLAN] as string)
      ?.toLowerCase()
      ?.split(',') || [];
  const categoryUrlParams =
    (queryParams[CATALOGUE.FILTER_TYPES.CATEGORY] as string)
      ?.toLowerCase()
      ?.split(',') || [];
  const brandUrlParams =
    (queryParams[CATALOGUE.FILTER_TYPES.BRAND] as string)
      ?.toLowerCase()
      ?.split(',') || [];
  const filterData = [];
  const ippName = t('TEXT_IPP');
  if (ippOptions?.length) {
    const planFilter: FilterGroup = {
      id: CATALOGUE.FILTER_TYPES.PLAN,
      type: CATALOGUE.FILTER_TYPES.PLAN,
      name: t('TEXT_IPP_GROUP') as string,
      items: ippOptions.map(ippOption => {
        return {
          name: ippOption.text,
          value: ippOption.value,
          selected: ippUrlParams.includes(slugify(ippName.toLowerCase()))
        };
      })
    };
    filterData.push(planFilter);
  }

  if (categories?.length) {
    const categoryFilter: FilterGroup = {
      id: CATALOGUE.FILTER_TYPES.CATEGORY,
      type: CATALOGUE.FILTER_TYPES.CATEGORY,
      name: t('CATEGORIES') as string,
      items: categories.map(category => ({
        name: category.text,
        value: category.value.toLowerCase(),
        selected: categoryUrlParams.includes(
          slugify(category.text.toLowerCase())
        )
      }))
    };
    filterData.push(categoryFilter);
  }

  if (brands?.length) {
    brands.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0));
    const brandFilter: FilterGroup = {
      id: CATALOGUE.FILTER_TYPES.BRAND,
      type: CATALOGUE.FILTER_TYPES.BRAND,
      name: t('BRANDS') as string,
      items: brands.map(brand => ({
        name: brand.text,
        value: brand.value,
        selected: brandUrlParams.includes(slugify(brand.text.toLowerCase()))
      }))
    };
    filterData.push(brandFilter);
  }

  return filterData;
};

export const getSimOnlyPlans = (plans: Plan[] = []) => {
  return plans.filter(
    plan =>
      plan.groupName.toUpperCase() ===
      CATALOGUE.PLANS_GROUP_NAME.SIM_ONLY_PLUS.toUpperCase()
  );
};

export const getPhonePlans = (
  plans: Plan[],
  cisRates?: CisPlanRates[]
): Plan[] => {
  const phonePlans = plans
    .filter(plan => plan.enabledOnPhoneCatalog)
    .map(plan => {
      return {
        ...plan,
        text: plan.planName,
        value: plan.mecId,
        selected: plan.selected
      };
    });

  if (cisRates?.length) {
    return phonePlans.reduce((result, plan) => {
      const matched = cisRates.find(
        cisPlan => cisPlan.planID === plan.basePriceSchemaId
      );
      if (matched) {
        result.push({
          ...plan,
          monthlyCharges: matched?.discountedPrice || plan.monthlyCharges
        });
      }

      return result;
    }, []);
  }

  return phonePlans;
};
export const getDefaultPhonePlan = (
  plans: Plan[],
  cisRates?: CisPlanRates[]
): Plan => {
  const phonePlanFilters = getPhonePlans(plans, cisRates);
  return phonePlanFilters.find(f => f.selected) || phonePlanFilters[0];
};

export const getSelectedPlan = ({
  plans,
  location,
  queryParams,
  fromQueryParams = true,
  cisRates,
  selectedPlan
}: {
  plans: Plan[];
  location: Location;
  queryParams?: KeyValue;
  fromQueryParams?: boolean;
  cisRates?: CisPlanRates[];
  selectedPlan?: Plan;
}) => {
  const phonePlanFilters = getPhonePlans(plans, cisRates);
  const defaultPlan = getDefaultPhonePlan(plans, cisRates);
  if (!phonePlanFilters.find(f => f.value === defaultPlan?.mecId)) {
    return defaultPlan;
  }

  if (selectedPlan) {
    return selectedPlan;
  }

  if (queryParams?.plan && fromQueryParams) {
    const queryParamFilters = getFiltersFromQueryParams(
      phonePlanFilters,
      getSimOnlyPlans(plans),
      location.search
    );
    if (queryParamFilters.plan) {
      return queryParamFilters.plan;
    }
  }
  return defaultPlan;
};

const getLowestSkuPrice = (product: Product, plan: Plan) => {
  return Math.min(
    ...product?.prices
      .filter(price => price.planId === plan?.mecId)
      .map(plan => plan.publicPrice)
  );
};

export const filterProduct = ({
  products,
  filterData,
  selectedPlan,
  options = {
    byBrand: true,
    byCategory: true,
    byPlan: true
  }
}: {
  products: FormattedProduct[];
  filterData: FilterGroup[];
  selectedPlan: Plan;
  options?: {
    byBrand: boolean;
    byCategory: boolean;
    byPlan: boolean;
  };
}): FormattedProduct[] => {
  const filter = getSelectedFilters(filterData);
  const filterCriteria = {
    brands: filter.brandFilters,
    categories: filter.categoryFilters,
    instalmentPlans: filter.planFilters
  };
  return products
    .filter(product => {
      const lowestSkuPrice = getLowestSkuPrice(product, selectedPlan);
      const brandCondition =
        filterCriteria.brands?.length && options.byBrand
          ? filterCriteria.brands.includes(product.brand?.toLowerCase())
          : true;
      const categoryCondition =
        filterCriteria.categories?.length && options.byCategory
          ? !!filterCriteria.categories.find(category =>
              isProductHasTag(product, category)
            )
          : true;

      const instalmentPlansCon =
        filterCriteria.instalmentPlans?.length && options.byPlan
          ? !!filterCriteria.instalmentPlans.find(instalmentPlan =>
              isProductHasTag(product, instalmentPlan)
            )
          : true;

      return (
        !(product.isRoi || product.groupCampaignId) &&
        brandCondition &&
        categoryCondition &&
        instalmentPlansCon &&
        isFinite(lowestSkuPrice) &&
        lowestSkuPrice >= 0
      );
    })
    .sort(sortByRankingAndTitle);
};

export const checkIppChanged = (
  currentFilterData: FilterGroup[],
  newFilterData: FilterGroup[]
): { isIPPChanged: boolean; newValue: boolean } => {
  const currentSelectedFilters = getSelectedFilters(currentFilterData);
  const newSelectedFilters = getSelectedFilters(newFilterData);
  const oldValue = currentSelectedFilters.planFilters[0];
  const newValue = newSelectedFilters.planFilters[0];
  const isIPPChanged = oldValue !== newValue;
  return {
    isIPPChanged,
    newValue: !!newValue
  };
};

export const updateUrlParamFromFilter = (
  filterData: FilterGroup[],
  selectedPlan: Plan,
  queryParams: KeyValue
) => {
  if (!filterData.length) return;
  const urlParams = [
    `${CONSTANTS.URL_PARAMS.PLAN}=${slugify(
      selectedPlan?.planName.toLowerCase()
    )}`
  ];
  filterData?.forEach(filterGroup => {
    const selectedFilters = filterGroup.items.filter(
      filterItem => filterItem.selected
    );
    if (selectedFilters.length > 0) {
      urlParams.push(
        `${filterGroup.type}=${selectedFilters
          .map(item => slugify(item.name.toLowerCase()))
          .join(',')}`
      );
    }
  });
  Object.keys(queryParams).forEach(key => {
    if (
      ![
        CATALOGUE.FILTER_TYPES.CATEGORY,
        CATALOGUE.FILTER_TYPES.PLAN,
        CATALOGUE.FILTER_TYPES.BRAND,
        CONSTANTS.URL_PARAMS.PLAN
      ].includes(key)
    ) {
      urlParams.push(`${key}=${queryParams[key]}`);
    }
  });
  window.history.replaceState(null, null, `?${urlParams.join('&')}`);
};
